import type {
	Circle as LeafletCircle,
	Polygon as LeafletPolygon,
} from "leaflet";
import L from "leaflet";
import "leaflet-editable";
import { useCallback, useEffect, useRef } from "react";
import { DRAW_PATH_OPTIONS } from "../constants";
import { useFenceEditableLayer } from "../hooks/use-fence-editable-layer";
import { useFenceStore } from "../store/fence-store";
import type { DrawingHandlerProps } from "../types/components.types";
import { updatePolygonPreviewOnMap } from "../utils/fence-polygon-preview";

export const DrawingHandler = ({ mapRef }: DrawingHandlerProps) => {
	const {
		drawingMode,
		tempPolygon,
		tempCircle,
		isEditingFence,
		isRedrawingEdit,
		setTempCircle,
		setTempPolygon,
		addPolygonPoint,
		clearTempPolygon,
		cancelDrawing,
		setIsDraggingCircle,
		setCircleDragStart,
	} = useFenceStore();

	const drawingLayerRef = useRef<L.Circle | L.Polygon | L.Layer | null>(null);
	const tempCircleRef = useRef<LeafletCircle | null>(null);
	const tempPolygonRef = useRef<LeafletPolygon | null>(null);
	const editableLayerRef = useRef<LeafletCircle | LeafletPolygon | null>(null);
	const tempLineRef = useRef<L.Polyline | null>(null);
	const tempMarkerRefs = useRef<L.CircleMarker[]>([]);
	const isDrawingCircleRef = useRef(false);
	const tempCircleCenterRef = useRef<[number, number] | null>(null);

	const clearTempLayers = useCallback(() => {
		const map = mapRef.current;
		if (!map) {
			return;
		}

		if (drawingLayerRef.current) {
			map.removeLayer(drawingLayerRef.current);
			drawingLayerRef.current = null;
		}
		if (tempCircleRef.current) {
			map.removeLayer(tempCircleRef.current);
			tempCircleRef.current = null;
		}
		if (tempPolygonRef.current) {
			map.removeLayer(tempPolygonRef.current);
			tempPolygonRef.current = null;
		}
		if (tempLineRef.current) {
			map.removeLayer(tempLineRef.current);
			tempLineRef.current = null;
		}
		for (const m of tempMarkerRefs.current) {
			map.removeLayer(m);
		}
		tempMarkerRefs.current = [];
		tempCircleCenterRef.current = null;
	}, [mapRef]);

	const clearEditableLayer = useCallback(() => {
		const map = mapRef.current;
		if (!map) {
			return;
		}

		const layer = editableLayerRef.current;
		if (!layer) {
			return;
		}

		const layerWithEditing = layer as L.Layer & {
			disableEdit?: () => void;
			editing?: { disable?: () => void };
		};
		layerWithEditing.disableEdit?.();
		layerWithEditing.editing?.disable?.();
		map.removeLayer(layer);
		editableLayerRef.current = null;
	}, [mapRef]);

	const updatePolygonPreview = useCallback(
		(points: [number, number][]) => {
			const map = mapRef.current;
			if (!map) {
				return;
			}
			updatePolygonPreviewOnMap({
				map,
				points,
				drawingMode,
				refs: {
					tempPolygonRef,
					tempLineRef,
					tempMarkerRefs,
				},
				clearTempPolygon,
				addPolygonPoint,
			});
		},
		[mapRef, drawingMode, clearTempPolygon, addPolygonPoint]
	);

	const updateCirclePreview = useCallback(
		(center: [number, number] | null, radius: number | null) => {
			const map = mapRef.current;
			if (!map) {
				return;
			}

			if (tempCircleRef.current) {
				map.removeLayer(tempCircleRef.current);
				tempCircleRef.current = null;
			}

			if (center && radius && radius > 0) {
				tempCircleRef.current = L.circle([center[0], center[1]], {
					radius,
					...DRAW_PATH_OPTIONS,
				}).addTo(map);
			}
		},
		[mapRef]
	);

	useEffect(() => {
		const map = mapRef.current;
		if (!map) {
			return;
		}

		clearTempLayers();
		isDrawingCircleRef.current = false;
		setTempCircle(null);
		clearTempPolygon();

		if (!drawingMode || (isEditingFence && !isRedrawingEdit)) {
			return;
		}

		if (drawingMode === "polygon") {
			const handleMapClick = (e: L.LeafletMouseEvent) => {
				const latlng = e.latlng;
				addPolygonPoint([latlng.lat, latlng.lng]);
			};

			map.on("click", handleMapClick);

			return () => {
				map.off("click", handleMapClick);
				clearTempLayers();
			};
		}

		if (drawingMode === "circle") {
			const handleMapClick = (e: L.LeafletMouseEvent) => {
				if (isDrawingCircleRef.current) {
					isDrawingCircleRef.current = false;
					tempCircleCenterRef.current = null;
					setIsDraggingCircle(false);
					setCircleDragStart(null);
				} else {
					isDrawingCircleRef.current = true;
					const center: [number, number] = [e.latlng.lat, e.latlng.lng];
					tempCircleCenterRef.current = center;
					setCircleDragStart(center);
					setIsDraggingCircle(true);
					setTempCircle({
						center,
						radius: 0,
					});
				}
			};

			const handleMapMouseMove = (e: L.LeafletMouseEvent) => {
				if (isDrawingCircleRef.current && tempCircleCenterRef.current) {
					const center = tempCircleCenterRef.current;
					const distance = map.distance(
						L.latLng(center[0], center[1]),
						e.latlng
					);
					setTempCircle({
						center,
						radius: distance,
					});
				}
			};

			map.on("click", handleMapClick);
			map.on("mousemove", handleMapMouseMove);

			return () => {
				map.off("click", handleMapClick);
				map.off("mousemove", handleMapMouseMove);
				clearTempLayers();
			};
		}
	}, [
		drawingMode,
		isEditingFence,
		isRedrawingEdit,
		setTempCircle,
		clearTempPolygon,
		addPolygonPoint,
		setIsDraggingCircle,
		setCircleDragStart,
		clearTempLayers,
		mapRef,
	]);

	useEffect(() => {
		if (drawingMode === "polygon" && !(isEditingFence && !isRedrawingEdit)) {
			updatePolygonPreview(tempPolygon);
		}
	}, [
		tempPolygon,
		drawingMode,
		isEditingFence,
		isRedrawingEdit,
		updatePolygonPreview,
	]);

	useEffect(() => {
		if (drawingMode === "circle" && !(isEditingFence && !isRedrawingEdit)) {
			updateCirclePreview(
				tempCircle?.center ?? null,
				tempCircle?.radius ?? null
			);
		}
	}, [
		tempCircle,
		drawingMode,
		isEditingFence,
		isRedrawingEdit,
		updateCirclePreview,
	]);

	useFenceEditableLayer({
		mapRef,
		editableLayerRef,
		clearTempLayers,
		clearEditableLayer,
		drawingMode,
		isEditingFence,
		isRedrawingEdit,
		setTempCircle,
		setTempPolygon,
	});

	useEffect(() => {
		const handleWindowKeyDown = (e: Event) => {
			if (!(e instanceof globalThis.KeyboardEvent)) {
				return;
			}
			if (e.key === "Escape" && drawingMode) {
				cancelDrawing();
			}
		};

		window.addEventListener("keydown", handleWindowKeyDown);
		return () => window.removeEventListener("keydown", handleWindowKeyDown);
	}, [drawingMode, cancelDrawing]);

	if (!drawingMode) {
		return null;
	}

	return null;
};
